<template>
    <div class="forum-widget">
        <div class="forum-header">
            <h3 class="widget-title">{{ t('widgets.forum.title') }}</h3>
            <p v-if="isForumActive" class="widget-subtitle">{{ t('widgets.forum.description') }}</p>
            <div v-else class="forum-closed-notice">
                <div class="closed-content">
                    <svg class="closed-icon" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
                    </svg>
                    <span class="closed-message">This forum has been closed by the moderator.</span>
                </div>
                <p class="closed-description">You can still view previous messages, but new submissions are disabled.</p>
            </div>
        </div>

        <!-- Submit Entry Form -->
        <div v-if="isForumActive" class="new-entry-form">
            <h4 class="form-title">{{ t('widgets.forum.joinDiscussion') }}</h4>
            <div class="form-fields">
                <!-- Show name display and anonymous toggle only if forumName is not already "Anonymous" -->
                <div v-if="!isForumNameAnonymous" class="name-display-field">
                    <div class="name-info">
                        <span class="posting-as-label">{{ t('widgets.forum.postingAs') }}:</span>
                        <span class="current-name">{{ displayUserName }}</span>
                    </div>
                    <div class="form-check">
                        <input
                            v-model="postAsAnonymous"
                            type="checkbox"
                            class="form-check-input"
                            id="anonymousCheck"
                            :disabled="isSubmitting"
                        />
                        <label class="form-check-label" for="anonymousCheck">
                            {{ t('widgets.forum.postAsAnonymous') }}
                        </label>
                    </div>
                </div>
                <textarea
                    v-model="entryText"
                    :placeholder="t('widgets.forum.placeholder')"
                    rows="3"
                    maxlength="1000"
                    class="form-control"
                    :disabled="isSubmitting"
                ></textarea>
                <div class="form-footer">
                    <span class="char-count">
                        {{ entryText.length }}/1000 {{ t('widgets.forum.characters') }}
                    </span>
                    <button
                        @click="submitEntry"
                        :disabled="!canSubmit"
                        class="btn btn-warning"
                    >
                        {{ isSubmitting ? t('widgets.forum.posting') : t('widgets.forum.post') }}
                    </button>
                </div>
            </div>
        </div>

        <!-- Forum Entries -->
        <div v-if="entries.length > 0" class="forum-entries">
            <h4 class="entries-title">{{ t('widgets.forum.recentPosts') }}</h4>
            <div class="entries-container">
                <div 
                    v-for="entry in displayedEntries" 
                    :key="entry.id"
                    :class="[
                        'entry-item',
                        { 'entry-nested': entry.level > 0 },
                        { 'entry-deleted': entry.deleted_at }
                    ]"
                    :style="{ 
                        marginLeft: entry.level > 0 ? `${Math.min(entry.level * 2, 8)}rem` : '0'
                    }"
                >
                    <!-- Entry header with user info -->
                    <div class="entry-header">
                        <div class="user-info">
                            <div class="user-avatar">
                                <div v-if="entry.picture" class="avatar-image">
                                    <img :src="entry.picture" :alt="entry.user_name" />
                                </div>
                                <div v-else class="avatar-placeholder">
                                    <svg class="avatar-icon" fill="currentColor" viewBox="0 0 20 20">
                                        <path fill-rule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clip-rule="evenodd"></path>
                                    </svg>
                                </div>
                            </div>
                            <div class="user-details">
                                <div class="user-name-row">
                                    <p class="user-name">{{ entry.user_name || t('widgets.forum.anonymous') }}</p>
                                    <span v-if="entry.level > 0" class="level-badge">
                                        L{{ entry.level }}
                                    </span>
                                </div>
                                <div class="entry-meta">
                                    <span class="entry-date">{{ formatDate(entry.created_at) }}</span>
                                    <span v-if="entry.is_moderator" class="moderator-badge">
                                        <svg class="moderator-icon" fill="currentColor" viewBox="0 0 20 20">
                                            <path fill-rule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
                                        </svg>
                                        Moderator
                                    </span>
                                </div>
                            </div>
                        </div>
                        
                        <!-- Reply indicator -->
                        <div v-if="entry.level > 0" class="reply-indicator">
                            <svg class="reply-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6"></path>
                            </svg>
                            {{ t('widgets.forum.reply') }}
                            <span v-if="getParentUserName(entry)" class="reply-to">
                                {{ t('widgets.forum.to') }} {{ getParentUserName(entry) }}
                            </span>
                        </div>
                    </div>

                    <!-- Entry content -->
                    <div class="entry-content">
                        <p v-if="!entry.deleted_at" class="entry-text">{{ entry.text }}</p>
                        <p v-else class="entry-deleted-text">{{ t('forum.moderatedContent') }}</p>
                    </div>

                    <!-- Actions -->
                    <div class="entry-actions">
                        <button
                            v-if="!entry.deleted_at"
                            @click="showReplyForm(entry.id)"
                            class="btn btn-outline-warning btn-sm reply-btn"
                        >
                            <svg class="action-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6"></path>
                            </svg>
                            <span>{{ t('widgets.forum.reply') }}</span>
                        </button>
                    </div>

                    <!-- Reply Form (shown when replying to this entry) -->
                    <div v-if="replyingTo === entry.id" class="reply-form">
                        <div class="reply-context">
                            <div class="reply-info">
                                {{ t('widgets.forum.replyingTo') }} {{ entry.user_name || t('widgets.forum.anonymous') }}
                            </div>
                            <div class="reply-quote">
                                "{{ entry.text.length > 50 ? entry.text.substring(0, 50) + '...' : entry.text }}"
                            </div>
                        </div>
                        <div class="reply-fields">
                            <!-- Name display is already shown in main form, no need to repeat -->
                            <textarea
                                v-model="replyText"
                                :placeholder="t('widgets.forum.replyPlaceholder')"
                                rows="2"
                                maxlength="1000"
                                class="form-control"
                                :disabled="isSubmitting"
                            ></textarea>
                            <div class="reply-footer">
                                <span class="char-count">
                                    {{ replyText.length }}/1000 {{ t('widgets.forum.characters') }}
                                </span>
                                <div class="reply-buttons">
                                    <button
                                        @click="cancelReply"
                                        class="btn btn-outline-secondary btn-sm"
                                    >
                                        {{ t('widgets.forum.cancel') }}
                                    </button>
                                    <button
                                        @click="submitReply(entry.id)"
                                        :disabled="!canSubmitReply"
                                        class="btn btn-warning btn-sm"
                                    >
                                        {{ isSubmitting ? t('widgets.forum.posting') : t('widgets.forum.reply') }}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            
            <!-- Load More Button -->
            <div v-if="hasMoreEntries" class="load-more-section">
                <button
                    @click="loadMoreEntries"
                    :disabled="isLoadingMore"
                    class="btn btn-outline-warning"
                >
                    {{ isLoadingMore ? t('widgets.forum.loading') : t('widgets.forum.loadMore') }}
                </button>
            </div>
        </div>

        <div v-else class="status-message">
            <svg class="status-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" 
                      d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a2 2 0 01-2-2v-6a2 2 0 012-2h8z" />
            </svg>
            <p class="status-title">No messages yet. Start the conversation!</p>
        </div>

        <div v-if="error" class="alert alert-danger">
            {{ error }}
        </div>
    </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useApi } from '@/composables/useApi.js'
import { useWebSocket } from '@/composables/useWebSocket.js'
import { useAudienceConfig } from '@/composables/useAudienceConfig.js'
import { validateConfig } from '@/utils/config.js'

const { t } = useI18n()

const props = defineProps({
    forum: {
        required: false,
        default: null
    },
    sessionId: {
        required: true
    },
    // External configuration props
    apiBaseUrl: {
        default: ''
    },
    pusherConfig: {
        default: () => ({})
    },
    authToken: {
        default: null
    },
    congressId: {
        default: null
    },
    forumName: {
        default: 'Anonymous'
    }
})

const emit = defineEmits(['entry-submitted', 'session-stopped', 'session-started'])

// Configuration setup - use the new composable for consistent config
const config = useAudienceConfig(props)

// Validate configuration
const configValidation = computed(() => validateConfig(config.value))

// Setup API client
const { api } = useApi(config.value)

// Setup WebSocket
const { createEcho, subscribeToChannel } = useWebSocket(config.value)

// Reactive state
const entryText = ref('')
const userName = ref('')
const entries = ref([])
const forumItem = ref(props.forum || {})
const isSubmitting = ref(false)
const isLoadingMore = ref(false)
const error = ref('')
const currentPage = ref(1)
const hasMoreEntries = ref(true)
const isForumActive = ref(true)

// Anonymous posting toggle
const postAsAnonymous = ref(false)

// Reply functionality
const replyingTo = ref(null)
const replyText = ref('')

// Check if forumName is already "Anonymous" (case-insensitive)
const isForumNameAnonymous = computed(() => {
    return props.forumName.trim().toLowerCase() === 'anonymous'
})

// Display name shown to user
const displayUserName = computed(() => {
    if (postAsAnonymous.value) {
        return 'Anonymous'
    }
    return props.forumName
})

// Initialize userName based on forumName prop
const initializeUserName = () => {
    if (isForumNameAnonymous.value) {
        // If forumName is already "Anonymous", always use it
        userName.value = 'Anonymous'
    } else {
        // Otherwise, use forumName but allow anonymous toggle
        userName.value = props.forumName
    }
    console.log('ForumWidget: Initialized userName to:', userName.value)
}

// Real-time updates
let channel = null
let echo = null

// Initialize real-time updates using useWebSocket composable
const initializePusher = () => {
    console.log("🚀 INITIALIZE FORUM PUSHER - START")
    console.log("📋 Current config:", config.value)
    
    try {
        // Use the imported composable
        console.log("📡 Creating Echo instance with config:", config.value)
        echo = createEcho()
        
        if (!echo) {
            console.warn('❌ Failed to create Echo instance, real-time updates disabled')
            return
        }
        
        console.log("✅ Echo instance created successfully")

        // Subscribe to the session channel
        let finalCongressId = props.congressId

        finalCongressId = parseInt(finalCongressId)
        if (isNaN(finalCongressId)) {
            console.error('ForumWidget: Invalid congressId format, using fallback 1657')
            finalCongressId = 1657
        }
        
        const channelName = `session.${finalCongressId}.${props.sessionId}`
        console.log('Forum Widget subscribing to channel:', channelName)
        console.log('Forum Widget ✅ Using congress_id:', finalCongressId)
        
        // Use the subscribeToChannel composable
        channel = subscribeToChannel(echo, channelName, {
            'forum.started': (data) => {
                console.log('Forum session started:', data)
                error.value = ''
                if (data.forum) {
                    handleForumStarted(data)
                }
            },
            
            'forum.stopped': (data) => {
                console.log('Forum session stopped:', data)
                handleForumStopped(data)
            },
            
            'forum.entry.submitted': (data) => {
                console.log('Forum entry submitted:', data)
                handleNewEntry(data)
            },
            
            'forum.entry.moderated': (data) => {
                console.log('Forum entry moderated:', data.entry?.id, 'action:', data.action)
                handleEntryModerated(data)
            }
        })
        
        console.log("✅ ForumWidget: WebSocket channel setup complete")
    } catch (error) {
        console.error('Error initializing WebSocket for Forum Widget:', error)
    }
}

const cleanupPusher = () => {
    if (channel) {
        try {
            channel.stopListening()
        } catch (error) {
            console.log('Channel cleanup handled by composable')
        }
    }
    // The useWebSocket composable handles Echo cleanup internally
    // Don't disconnect Echo if it's the global instance
    if (echo && echo !== window.Echo) {
        echo.disconnect()
    }
}

// Computed
const forum = computed(() => forumItem.value)

const canSubmit = computed(() => {
    return entryText.value.trim().length > 0 && !isSubmitting.value && isForumActive.value
})

const canSubmitReply = computed(() => {
    return replyText.value.trim().length > 0 && !isSubmitting.value && isForumActive.value
})

const displayedEntries = computed(() => {
    const publicEntries = entries.value
        .filter(entry => entry && !entry.private) // Only show public entries to audience
    
    if (publicEntries.length === 0) return []
    
    // Build threaded structure
    const entryMap = new Map()
    publicEntries.forEach(entry => {
        entryMap.set(entry.id, { 
            ...entry, 
            children: [],
            level: 0
        })
    })
    
    const rootEntries = []
    
    publicEntries.forEach(entry => {
        const entryData = entryMap.get(entry.id)
        const replyToId = entry.is_reply_to ? parseInt(entry.is_reply_to) : null
        const hasParent = replyToId && entryMap.has(replyToId)
        
        if (hasParent) {
            const parent = entryMap.get(replyToId)
            parent.children.push(entryData)
        } else {
            rootEntries.push(entryData)
        }
    })
    
    // Recursively assign levels and flatten
    const flattenWithLevels = (entries, level = 0) => {
        const result = []
        
        entries.forEach(entry => {
            entry.level = level
            result.push(entry)
            
            if (entry.children && entry.children.length > 0) {
                const sortedChildren = [...entry.children].sort((a, b) => 
                    new Date(a.created_at) - new Date(b.created_at)
                )
                const childResults = flattenWithLevels(sortedChildren, level + 1)
                result.push(...childResults)
            }
        })
        
        return result
    }
    
    const sortedRoots = [...rootEntries].sort((a, b) => 
        new Date(b.created_at) - new Date(a.created_at)
    )
    
    return flattenWithLevels(sortedRoots, 0)
})

// Methods
const loadForumItem = async () => {
    try {
        console.log("🔍 Loading Forum item with API baseURL:", config.value.apiBaseUrl)
        const response = await api.get(`/api/sessions/${props.sessionId}/forum`)
        
        // API returns { forum: {...}, entries: [...] }, extract the forum object
        const responseData = response.data || {}
        if (responseData.forum) {
            console.log("✅ Extracting forum from response.data.forum")
            forumItem.value = responseData.forum
            // Also store entries if provided
            if (responseData.entries) {
                entries.value = responseData.entries
            }
        } else {
            // Fallback: if response is already the forum object
            forumItem.value = responseData
        }
        
        console.log("✅ Forum item loaded successfully:", forumItem.value)
        console.log("🔍 Forum item ID:", forumItem.value.id)
        console.log("🔍 Forum item active status:", !forumItem.value.stopped_at)
        
        isForumActive.value = !forumItem.value.stopped_at
        
        // Once forumItem is loaded, check if we need to load entries
        if (forumItem.value && forumItem.value.id) {
            console.log("🚀 Forum item has ID...")
            // Only load entries if they weren't provided in the response
            if (!responseData.entries) {
                console.log("🔄 Loading entries separately...")
                await loadEntries()
            } else {
                console.log("✅ Entries already loaded from API response:", entries.value.length)
            }
            
            // Check if forum is active and emit event (SAME AS QANDA/VOTING)
            if (isForumActive.value) {
                console.log("✅ Forum is active, emitting session-started event")
                emit('session-started', { forum: forumItem.value })
            } else {
                console.log("ℹ️ Forum exists but has been stopped")
                // Don't emit session-stopped - just log it (SAME AS QANDA/VOTING)
            }
        } else {
            console.warn("⚠️ Forum item loaded but no ID found:", forumItem.value)
        }
        
    } catch (err) {
        console.error('❌ Error loading Forum item:', err)
        console.error('📡 API config being used:', { 
            baseURL: config.value.apiBaseUrl,
            fullURL: `${config.value.apiBaseUrl}/api/sessions/${props.sessionId}/forum`
        })
    }
}

const handleForumStarted = (data) => {
    console.log('ForumWidget: Handling forum started:', data)
    isForumActive.value = true
    error.value = ''
    if (data.forum) {
        forumItem.value = data.forum
    }
    loadEntries()
    // Emit event to parent component
    emit('session-started', { forum: data.forum || forumItem.value })
}

const handleForumStopped = (data) => {
    console.log('ForumWidget: Handling forum stopped:', data)
    isForumActive.value = false
    entryText.value = ''
    error.value = ''
    // Emit event to parent component to hide the widget
    emit('session-stopped', { forum: data.forum || forumItem.value })
}

const handleNewEntry = (data) => {
    console.log('ForumWidget: Handling new entry:', data)
    
    if (data.entry && data.entry.forum_id === forumItem.value.id) {
        const exists = entries.value.find(e => e.id === data.entry.id)
        if (!exists) {
            entries.value.unshift(data.entry)
            console.log('ForumWidget: Added new entry from broadcast:', data.entry.id)
        }
    }
}

const handleEntryModerated = (data) => {
    if (data.entry && data.entry.forum_id === forumItem.value.id) {
        const entryIndex = entries.value.findIndex(e => e.id == data.entry.id || e.id === data.entry.id)
        
        if (entryIndex !== -1) {
            if (data.action === 'delete') {
                const removedEntry = entries.value.splice(entryIndex, 1)
                console.log('ForumWidget: Removed entry from audience view:', removedEntry[0]?.id)
            } else if (data.action === 'restore') {
                const restoredEntry = { ...entries.value[entryIndex], ...data.entry, deleted_at: null }
                entries.value[entryIndex] = restoredEntry
                console.log('ForumWidget: Restored entry in audience view:', data.entry.id)
            }
        } else {
            if (data.action === 'restore') {
                const restoredEntry = { ...data.entry, deleted_at: null }
                entries.value.unshift(restoredEntry)
                console.log('ForumWidget: Added restored entry to audience view:', data.entry.id)
            }
        }
    }
}

const submitEntry = async () => {
    if (!canSubmit.value || !isForumActive.value) return

    // Check if we have the forumItem loaded with an ID
    if (!forumItem.value || !forumItem.value.id) {
        error.value = 'Forum not loaded. Please wait a moment and try again.'
        return
    }

    isSubmitting.value = true
    error.value = ''

    try {
        console.log('ForumWidget: Submitting entry to forum:', forumItem.value.id)
        const response = await api.post(`/api/audience/forum/${forumItem.value.id}/entries`, {
            text: entryText.value.trim(),
            user_name: userName.value.trim() || null
        })

        console.log('ForumWidget: Entry submitted successfully:', response.data)
        entryText.value = ''

        emit('entry-submitted', {
            forum_id: forumItem.value.id,
            entry: response.data
        })

    } catch (err) {
        console.error('Error submitting entry:', err)
        
        if (err.response?.status === 429) {
            error.value = 'Please wait before posting another message.'
        } else if (err.response?.status === 404) {
            error.value = 'This forum is no longer active.'
        } else {
            error.value = 'Failed to post message. Please try again.'
        }
    } finally {
        isSubmitting.value = false
    }
}

const getParentUserName = (entry) => {
    if (!entry.is_reply_to) return null
    
    const parent = entries.value.find(e => e.id === entry.is_reply_to)
    return parent ? (parent.user_name || 'Anonymous') : null
}

const showReplyForm = (entryId) => {
    replyingTo.value = entryId
    replyText.value = ''
}

const cancelReply = () => {
    replyingTo.value = null
    replyText.value = ''
}

const submitReply = async (parentEntryId) => {
    if (!canSubmitReply.value || !isForumActive.value) return

    // Check if we have the forumItem loaded with an ID
    if (!forumItem.value || !forumItem.value.id) {
        error.value = 'Forum not loaded. Please wait a moment and try again.'
        return
    }

    isSubmitting.value = true
    error.value = ''

    try {
        const response = await api.post(`/api/audience/forum/${forumItem.value.id}/entries`, {
            text: replyText.value.trim(),
            is_reply_to: parentEntryId,
            user_name: userName.value.trim() || null
        })

        replyingTo.value = null
        replyText.value = ''

        emit('entry-submitted', {
            forum_id: forumItem.value.id,
            entry: response.data
        })

    } catch (err) {
        console.error('Error submitting reply:', err)
        
        if (err.response?.status === 429) {
            error.value = 'Please wait before posting another message.'
        } else if (err.response?.status === 404) {
            error.value = 'This forum is no longer active.'
        } else {
            error.value = 'Failed to post reply. Please try again.'
        }
    } finally {
        isSubmitting.value = false
    }
}

const loadEntries = async (page = 1) => {
    // Wait for forumItem to be loaded first
    if (!forumItem.value || !forumItem.value.id) {
        console.log("⏳ Forum item not loaded yet, waiting...", forumItem)
        return
    }
    
    try {
        console.log("🔍 Loading entries with API baseURL:", config.value.apiBaseUrl)
        const response = await api.get(`/api/audience/forum/${forumItem.value.id}/entries?page=${page}`)
        
        const activeEntries = (response.data.entries || []).filter(entry => !entry.deleted_at)
        
        if (page === 1) {
            entries.value = activeEntries
        } else {
            entries.value.push(...activeEntries)
        }
        
        hasMoreEntries.value = response.data.has_more || false
        
        console.log('ForumWidget: Loaded entries (active only):', activeEntries.length)
        
        console.log("✅ Entries loaded successfully:", entries.value.length)
    } catch (err) {
        console.error('❌ Error loading entries:', err)
        console.error('📡 API config being used:', { 
            baseURL: config.value.apiBaseUrl,
            fullURL: `${config.value.apiBaseUrl}/api/audience/forum/${forumItem.value.id}/entries?page=${page}`
        })
    }
}

const loadMoreEntries = async () => {
    if (isLoadingMore.value || !hasMoreEntries.value) return

    isLoadingMore.value = true
    currentPage.value++

    try {
        await loadEntries(currentPage.value)
    } catch (err) {
        console.error('Error loading more entries:', err)
        currentPage.value--
    } finally {
        isLoadingMore.value = false
    }
}

const formatDate = (dateString) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffMs = now - date
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)
    
    if (diffMins < 1) return t('timeFormat.justNow')
    if (diffMins < 60) return `${diffMins}${t('timeFormat.minutesAgo')}`
    if (diffHours < 24) return `${diffHours}${t('timeFormat.hoursAgo')}`
    if (diffDays < 7) return `${diffDays}${t('timeFormat.daysAgo')}`
    return date.toLocaleDateString()
}

// Watch for changes in anonymous toggle
watch(postAsAnonymous, (isAnonymous) => {
    if (isAnonymous) {
        userName.value = 'Anonymous'
    } else {
        userName.value = props.forumName
    }
    console.log('ForumWidget: userName updated to:', userName.value, '(anonymous:', isAnonymous, ')')
})

// No need to watch userName since it's now controlled by forumName and postAsAnonymous

// Lifecycle
onMounted(() => {
    if (props.sessionId) {
        console.log('Forum Widget mounted, loading forum item...')
        console.log('Session ID:', props.sessionId)
        console.log('Forum Name prop:', props.forumName)
        
        // Initialize userName from forumName
        initializeUserName()
       
        loadForumItem() // This will load entries after forumItem is loaded
        initializePusher()
    } else {
        console.warn('Forum Widget mounted without valid sessionId, skipping initialization.')
    }
})

onUnmounted(() => {
    cleanupPusher()
})

// Expose methods for parent component access
defineExpose({
    handleNewEntry
})
</script>

<style scoped>
/* Forum Widget Bootstrap Styling */
.forum-widget {
    background: linear-gradient(135deg, #fff3e0 0%, #fce4ec 100%);
  
    padding: 1.5rem;
    margin-bottom: 1rem;
    box-shadow: 0 4px 12px rgba(255, 152, 0, 0.15);
    animation: slideIn 0.3s ease-out;
}

.forum-header {
    margin-bottom: 1.5rem;
    text-align: center;
}

.widget-title {
    font-size: 1.5rem;
    font-weight: 600;
    color: #e65100;
    margin-bottom: 0.5rem;
}

.widget-subtitle {
    color: #424242;
    margin-bottom: 0;
    font-size: 1rem;
}

/* Forum Closed Notice */
.forum-closed-notice {
    background: #f5f5f5;
    border: 2px solid #bdbdbd;
    border-radius: 0.5rem;
    padding: 1rem;
}

.closed-content {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    color: #616161;
    margin-bottom: 0.25rem;
}

.closed-icon {
    width: 1.25rem;
    height: 1.25rem;
}

.closed-message {
    font-weight: 500;
}

.closed-description {
    font-size: 0.875rem;
    color: #757575;
    margin: 0;
}

/* Form Styling */
.new-entry-form {
    margin-bottom: 1.5rem;
}

.form-title {
    font-weight: 600;
    color: #424242;
    margin-bottom: 0.75rem;
    font-size: 1.1rem;
}

.form-fields {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
}

/* Name Display Field */
.name-display-field {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    padding: 0.75rem;
    background: #fff3e0;
    border: 2px solid #ffb74d;
    border-radius: 0.5rem;
}

.name-info {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.95rem;
}

.posting-as-label {
    color: #757575;
    font-weight: 500;
}

.current-name {
    color: #e65100;
    font-weight: 600;
}

.form-check {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin: 0;
}

.form-check-input {
    width: 1.25rem;
    height: 1.25rem;
    border: 2px solid #ff9800;
    cursor: pointer;
}

.form-check-input:checked {
    background-color: #ff9800;
    border-color: #ff9800;
}

.form-check-input:focus {
    border-color: #ff9800;
    box-shadow: 0 0 0 0.2rem rgba(255, 152, 0, 0.25);
}

.form-check-label {
    color: #424242;
    font-size: 0.9rem;
    cursor: pointer;
    margin: 0;
}

.form-control {
    border: 2px solid #ffb74d;
    border-radius: 0.5rem;
    padding: 0.75rem;
    font-size: 1rem;
    transition: border-color 0.2s ease;
    resize: none;
}

.form-control:focus {
    border-color: #ff9800;
    box-shadow: 0 0 0 0.2rem rgba(255, 152, 0, 0.25);
    outline: none;
}

.form-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.5rem;
}

.char-count {
    font-size: 0.875rem;
    color: #6c757d;
}

/* Forum Entries */
.forum-entries {
    margin-top: 1rem;
}

.entries-title {
    font-weight: 600;
    color: #424242;
    margin-bottom: 1rem;
    font-size: 1.1rem;
}

.entries-container {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    max-height: 24rem;
    overflow-y: auto;
}

.entry-item {
    background: #ffffff;
    border: 2px solid #ffcc80;
    border-radius: 0.5rem;
    padding: 1rem;
    animation: fadeIn 0.5s ease-out;
    transition: all 0.2s ease;
}

.entry-item.entry-nested {
    border-left: 4px solid #ff9800;
    background: linear-gradient(135deg, #fff8f0 0%, #ffffff 100%);
}

.entry-item.entry-deleted {
    opacity: 0.5;
}

/* Entry Header */
.entry-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 0.75rem;
    gap: 1rem;
}

.user-info {
    display: flex;
    align-items: center;
    gap: 0.75rem;
}

.user-avatar {
    flex-shrink: 0;
}

.avatar-image {
    width: 2rem;
    height: 2rem;
    border-radius: 50%;
    overflow: hidden;
}

.avatar-image img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.avatar-placeholder {
    width: 2rem;
    height: 2rem;
    border-radius: 50%;
    background: #ffb74d;
    display: flex;
    align-items: center;
    justify-content: center;
}

.avatar-icon {
    width: 1.25rem;
    height: 1.25rem;
    color: #e65100;
}

.user-details {
    flex: 1;
}

.user-name-row {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 0.25rem;
}

.user-name {
    font-size: 0.875rem;
    font-weight: 500;
    color: #424242;
    margin: 0;
}

.level-badge {
    display: inline-flex;
    align-items: center;
    padding: 0.125rem 0.375rem;
    font-size: 0.75rem;
    background: #ffcc80;
    color: #e65100;
    border-radius: 0.25rem;
    font-weight: 600;
}

.entry-meta {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.75rem;
    color: #757575;
}

.entry-date {
    color: #757575;
}

.moderator-badge {
    display: inline-flex;
    align-items: center;
    padding: 0.25rem 0.5rem;
    font-size: 0.75rem;
    background: #fff3e0;
    color: #e65100;
    border-radius: 1rem;
    gap: 0.25rem;
}

.moderator-icon {
    width: 0.75rem;
    height: 0.75rem;
}

/* Reply Indicator */
.reply-indicator {
    display: flex;
    align-items: center;
    font-size: 0.75rem;
    color: #ff9800;
    gap: 0.25rem;
}

.reply-icon {
    width: 1rem;
    height: 1rem;
}

.reply-to {
    margin-left: 0.25rem;
}

/* Entry Content */
.entry-content {
    margin-bottom: 0.75rem;
}

.entry-text {
    color: #424242;
    line-height: 1.5;
    margin: 0;
    font-size: 1rem;
}

.entry-deleted-text {
    color: #757575;
    font-style: italic;
    margin: 0;
}

/* Entry Actions */
.entry-actions {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    gap: 0.5rem;
}

.reply-btn {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    padding: 0.25rem 0.75rem;
    font-size: 0.875rem;
    border-radius: 1.5rem;
    transition: all 0.2s ease;
    white-space: nowrap;
}

.action-icon {
    width: 1rem;
    height: 1rem;
}

/* Reply Form */
.reply-form {
    margin-top: 1rem;
    padding-top: 1rem;
    border-top: 2px solid #ffcc80;
}

.reply-context {
    margin-bottom: 0.75rem;
    padding: 0.5rem;
    background: #fff3e0;
    border-radius: 0.5rem;
    border-left: 4px solid #ff9800;
}

.reply-info {
    font-size: 0.875rem;
    color: #e65100;
    font-weight: 500;
}

.reply-quote {
    font-size: 0.75rem;
    color: #ff6f00;
    margin-top: 0.25rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.reply-fields {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
}

.reply-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.5rem;
}

.reply-buttons {
    display: flex;
    gap: 0.5rem;
}

/* Load More Section */
.load-more-section {
    text-align: center;
    margin-top: 1rem;
}

/* Status Message */
.status-message {
    text-align: center;
    padding: 2rem;
    color: #616161;
}

.status-icon {
    width: 3rem;
    height: 3rem;
    margin: 0 auto 1rem;
    color: #616161;
}

.status-title {
    font-weight: 500;
    color: #424242;
    margin: 0;
}

/* Animations */
@keyframes slideIn {
    from {
        opacity: 0;
        transform: translateY(20px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

@keyframes fadeIn {
    from { 
        opacity: 0;
        transform: scale(0.95);
    }
    to { 
        opacity: 1;
        transform: scale(1);
    }
}

/* Responsive Design */
@media (max-width: 768px) {
    .forum-widget {
        padding: 1rem;
    }
    
    .entry-header {
        flex-direction: column;
        align-items: flex-start;
        gap: 0.5rem;
    }
    
    .reply-indicator {
        align-self: flex-start;
    }
    
    .entry-actions {
        flex-direction: column;
        align-items: stretch;
        gap: 0.5rem;
    }
    
    .reply-footer {
        flex-direction: column;
        align-items: stretch;
        text-align: center;
    }
    
    .reply-buttons {
        justify-content: center;
    }
    
    .form-footer {
        flex-direction: column;
        align-items: stretch;
        text-align: center;
    }
}
</style>